import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getFilesTool from 'common/src/modules/shared/copilot/tools/getFilesTool';
import analyzeExcelHeadersTool from 'common/src/modules/shared/copilot/tools/analyzeExcelHeadersTool/analyzeExcelHeadersTool';
import readValuesFromExcelTool from '../../../../tools/general/readValuesFromExcelTool/readValuesFromExcelTool';
import deleteOutuputsTool from '../general/deleteOutputsTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {ScheduleCardScratchpadState} from '../../scratchpad/scheduleCardScratchpad.types';
import getOrCreateScheduleCard from '../../utils/schedule/getOrCreateScheduleCard';
import setScheduleTypeTool from './setScheduleTypeTool';
import setTimezoneSourceTool from './setTimezoneSourceTool';
import setTimeZoneTool from './setTimeZoneTool';
import setRandomizeOutputsTool from './setRandomizeOutputsTool';
import addScheduleOutputTool from './addScheduleOutputTool';
import editScheduleOutputTool from './editScheduleOutputTool';
import {FallbackOutputGuidelines} from '../../promptToCardAgentV2.consts';

const cardSpecificInstructions = `1. Use the ${V2CardAgentToolName.SetScheduleType} tool to set whether the schedule uses date ranges or days of week.
2. Use the ${V2CardAgentToolName.SetTimezoneSource} and ${V2CardAgentToolName.SetTimeZone} tools when the user specifies a timezone (timezone is only editable when source is Manual).
3. Use the ${V2CardAgentToolName.AddScheduleOutput} or ${V2CardAgentToolName.EditScheduleOutput} tool to add or edit schedule outputs. Outputs must match the schedule type (Date Range vs Days Of Week).
4. The user may ask to create outputs from an Excel file. Use the get-files tool to list files, then ${V2CardAgentToolName.ReadValuesFromExcel} to read values.

### Schedule card specific instructions
${FallbackOutputGuidelines}. 
- Example use case: "Route traffic to output 'Business Hours' Monday-Friday 9:00-17:00" (NO need to mention after-hours)
- When schedule type changes, all regular outputs are cleared; only add outputs that match the current schedule type.

### IMPORTANT
- DO NOT add fallback outputs; only set the outputs requested by the user.
- If the user did not ask to change the schedule type or timezone, skip those steps.`;

export default function addOrUpdateScheduleCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, ScheduleCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId: cardId} = preProcessStandaloneAgent(params, store);

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateScheduleCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.SCHEDULE]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.SCHEDULE,
        cardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getFilesTool(),
        getCardDetailsTool(),
        analyzeExcelHeadersTool(),
        readValuesFromExcelTool(),
        deleteOutuputsTool(),

        setScheduleTypeTool(),
        setTimezoneSourceTool(),
        setTimeZoneTool(),
        setRandomizeOutputsTool(),
        addScheduleOutputTool(),
        editScheduleOutputTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card: await getOrCreateScheduleCard(cardId, store)};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateScheduleCard,
    description:
      'Add or update a Schedule card. Supports date ranges or day-of-week/time ranges with configurable timezone (Manual, User, or Advertiser).',
    displayName: 'Working on schedule card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
